import { retryTransient } from '@agos/api-client'
import { useGetCentralAuthMe, usePostCentralAuthLogin } from '@agos/api-client/platform'
import { T, tt, useIsMobile, useUnsavedWorkGate } from '@agos/shared'
import { ConfirmDialog, Spinner, Toaster, cn } from '@agos/ui'
import {
  BarChart3,
  LifeBuoy,
  X,
  Building2,
  CreditCard,
  FileText,
  Gauge,
  LayoutDashboard,
  LogOut,
  Megaphone,
  Menu,
  Package,
  Radio as Tower,
  ScrollText,
  Store,
  ToggleLeft,
  TrendingUp,
  Users,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { Link, useLocation } from 'wouter'

import {
  loginFailureNote,
  maySubmitLogin,
  signInTower,
  signOutTower,
  towerSessionFrom,
} from '../auth/tower-session'

/**
 * The Control Tower's frame: the session gate, then the navigation.
 *
 * Nothing renders until `GET /central/auth/me` has answered, because every page behind this reads
 * cross-tenant data and a flash of it before the session is confirmed is a flash of other people's
 * restaurants.
 */

/**
 * The navigation, grouped and with an icon each - SAAS.md §3.78.
 *
 * **It was a flat list of thirteen labels in a wrapped row of `text-xs` links, and that was the
 * owner's complaint.** *"SaaS looks really weird, at all... I just hate that design overall"*, and
 * before that *"to also match our designs from pizzamajstro customer and manager."* The manager's
 * chrome is a dark sidebar of grouped sections with an icon per entry; this was a horizontal strip
 * with no icons, so the two products did not look like one product. The tokens were never the
 * problem - `index.css` is byte-identical to the manager's - the **shape** was.
 *
 * Each label stays inside its own `tt()` call rather than being looked up from a map of bare
 * strings: the extractor sees every one of them, and the lookup still happens at render.
 */
function navSections() {
  return [
    {
      heading: tt('Prevádzka'),
      items: [
        { href: '/', key: 'dashboard', label: tt('Prehľad'), Icon: LayoutDashboard },
        { href: '/franchises', key: 'franchises', label: tt('Franšízy'), Icon: Store },
        { href: '/stats', key: 'stats', label: tt('Štatistiky'), Icon: BarChart3 },
      ],
    },
    {
      heading: tt('Cenník'),
      items: [
        { href: '/packages', key: 'packages', label: tt('Balíky'), Icon: Package },
        { href: '/modules', key: 'modules', label: tt('Moduly'), Icon: ToggleLeft },
        // `/pricing` rather than `/api-pricing`: a Tower route beginning `/api` is caught by the
        // proxy prefix and never reaches the app. See App.tsx.
        { href: '/pricing', key: 'pricing', label: tt('Ceny API'), Icon: Gauge },
      ],
    },
    {
      heading: tt('Peniaze'),
      items: [
        { href: '/billing', key: 'billing', label: tt('Fakturácia'), Icon: FileText },
        { href: '/platform-billing', key: 'platform-billing', label: tt('Platforma'), Icon: Building2 },
        { href: '/cardpay', key: 'cardpay', label: 'CardPay', Icon: CreditCard },
        { href: '/profitability', key: 'profitability', label: tt('Ziskovosť'), Icon: TrendingUp },
        { href: '/partners', key: 'partners', label: tt('Partneri'), Icon: Users },
      ],
    },
    {
      heading: tt('Ostatné'),
      items: [
        { href: '/marketing', key: 'marketing', label: tt('Marketing'), Icon: Megaphone },
        /* §3.137, owner O31 - *"make in SaaS tickets visible"*. In *Ostatné* beside the audit log, which is
         * the other screen that is a record of what happened rather than a thing to configure. */
        { href: '/support-tickets', key: 'support-tickets', label: tt('Tikety'), Icon: LifeBuoy },
        { href: '/audit-log', key: 'audit-log', label: tt('Audit'), Icon: ScrollText },
      ],
    },
  ]
}

/** Every entry, flat - for anything that needs the set rather than the grouping. */
export function towerNavItems() {
  return navSections().flatMap((section) => section.items)
}

/**
 * The title a page should carry, from the sidebar entry that leads to it - §3.78.
 *
 * **One source for the menu label and the page heading**, so the two cannot disagree. They disagreed
 * completely before, because the pages had no heading at all.
 */
export function towerPageTitle(path: string): string {
  return towerNavItems().find((item) => item.href === path)?.label ?? 'Control Tower'
}

function TowerLogin() {
  const login = usePostCentralAuthLogin()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [problem, setProblem] = useState('')

  async function submit() {
    setProblem('')
    try {
      const answer = await login.mutateAsync({ data: { email: email.trim(), password } })
      const session = towerSessionFrom(answer)
      if (session === null) {
        setProblem(tt('Prihlásenie zlyhalo'))
        return
      }
      signInTower(session.token)
      // A full reload rather than a state flip: every query below this was created without a session,
      // and re-mounting the whole tree is both simpler and impossible to get half-right.
      window.location.assign('/')
    } catch (failure) {
      setProblem(loginFailureNote(failure))
    }
  }

  return (
    <main className="min-h-screen flex items-center justify-center p-4" data-tower-login>
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          void submit()
        }}
        className="w-full max-w-sm space-y-3 rounded-2xl border border-border bg-card p-6"
      >
        <h1 className="text-lg font-bold">Control Tower</h1>
        <input
          data-field="email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder={tt('E-mail')}
          className="w-full min-h-[44px] rounded-xl border border-border bg-background px-3 text-sm"
        />
        <input
          data-field="password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          placeholder={tt('Heslo')}
          className="w-full min-h-[44px] rounded-xl border border-border bg-background px-3 text-sm"
        />
        <button
          type="submit"
          data-submit
          disabled={!maySubmitLogin(email, password) || login.isPending}
          className="w-full min-h-[44px] rounded-xl bg-primary text-primary-foreground text-sm font-bold disabled:opacity-50"
        >
          <T>Prihlásiť sa</T>
        </button>
        {problem !== '' && (
          <p className="text-xs text-red-500" data-login-problem>
            {problem}
          </p>
        )}
      </form>
    </main>
  )
}

export function TowerShell(props: { children: ReactNode }) {
  const [location, navigate] = useLocation()
  /**
   * §3.173, FB-938 - the drawer the manager has had since §9.7 and the Tower never got.
   *
   * `useIsMobile` is the product's one answer to *is this a phone*, ported from legacy's own
   * `hooks/use-mobile.tsx` with its 768 px breakpoint. Legacy's super-admin uses `lg` (1024) for this
   * app; a second number here would be two answers to one question, and the Tower's pages already
   * carry Tailwind's own `sm:`/`md:` steps.
   *
   * **Closed on every navigation**, without which tapping an entry leaves the panel covering the screen
   * it just opened - and closed **at the entry** rather than in an effect on `location`. Legacy uses the
   * effect and lint refuses it here: *calling setState synchronously within an effect can trigger
   * cascading renders*. `AppLayout` does it at the entry too (`onNavigate={() => setDrawerOpen(false)}`),
   * so the house pattern and the lint rule agree, and the effect's extra render goes with them.
   */
  const isMobile = useIsMobile()
  const [drawerOpen, setDrawerOpen] = useState(false)
  /**
   * §3.175, FB-941 - the sidebar asks before it takes an editor's work with it.
   *
   * **`beforeunload` cannot see this.** `wouter` pushes the URL, the page unmounts and its draft goes with
   * it, with no document unload for the browser to fire on - and in a single-page application a click on the
   * sidebar is how somebody leaves a screen. Measured: four pages across the Tower and the manager arm
   * `beforeunload` and **none** guards a route change, because `wouter` has no blocker to reach for.
   *
   * **Read at the click rather than subscribed to**, which is why the gate is a function: a shell that
   * re-rendered on the answer would re-render on every keystroke in the editor it contains.
   *
   * `pending` is an action rather than a target, so one dialog covers both ways out of this sidebar - a nav
   * entry and **`data-sign-out`**, the second being the larger loss because it discards the session too.
   */
  const unsavedWork = useUnsavedWorkGate()
  const [pending, setPending] = useState<{ run: () => void } | null>(null)
  /** Runs `leave` unless there is work to lose, in which case the question is asked first. */
  const askFirst = (leave: () => void) => (event: { preventDefault: () => void }) => {
    if (!unsavedWork()) {
      leave()
      return
    }
    event.preventDefault()
    setPending({ run: leave })
  }
  /* FB-835, §3.159 - **this read decides whether the whole application renders, and `retry: false` meant one
     dropped packet signed the operator out.** A 401 is still final, which is what the setting was right about;
     `retryTransient` gives a 502 from the proxy or a lost connection a second attempt before the login form replaces
     whatever they were doing. Legacy's own super-admin is `retry: 1`, so retrying not at all was a divergence rather
     than a port. */
  const session = useGetCentralAuthMe({ query: { retry: retryTransient } })

  if (session.isLoading) {
    return (
      <main
        className="min-h-screen flex items-center justify-center bg-background"
        data-tower-loading
      >
        {/* §3.78 - the shared Spinner, which is what every manager screen shows while it waits. It
          * was an `sr-only` string, so a sighted person watching the session gate saw a blank page
          * and nothing else - the single clearest instance of *"does not look complete"*. The
          * screen-reader label is inside `Spinner` itself. */}
        <Spinner />
      </main>
    )
  }

  if (session.isError) return <TowerLogin />

  /**
   * **The session is nested under `user`, and the shell had never looked there - §3.78.**
   *
   * `GET /central/auth/me` answers `{"user": {id, name, email, systemRole, superAdmin}}`. This read
   * `session.data.email` directly, one level too shallow, so `data-tower-user` has been **empty on
   * every screen since it was written** - a blank space where the rest of the product names who is
   * signed in. Found because an e2e asserting the element was visible reported it *hidden*: an empty
   * element has no height.
   *
   * Read through the wrapper, with the name preferred and the address as the tooltip, which is what
   * the manager's own user menu shows.
   */
  const payload = (session.data as { user?: { email?: unknown; name?: unknown } } | undefined) ?? {}
  const user = payload.user ?? {}
  const userName = typeof user.name === 'string' && user.name !== '' ? user.name : ''
  const userEmail = typeof user.email === 'string' ? user.email : ''
  const whoIsSignedIn = userName !== '' ? userName : userEmail

  return (
    <div className="min-h-screen bg-background" data-tower-shell>
      {/* §3.78 - a sidebar, the same shape and the same tokens as the manager's. `data-tower-shell`,
        * `data-tower-user`, `data-sign-out` and every `data-nav` are kept exactly, because the e2e
        * suite addresses the shell through them and a rebuild that renamed its own hooks would be a
        * rewrite rather than a fix. */}
      {/* §3.173 - present on a desktop, and on a phone only while the drawer is open. The shadow is
        * what says it is floating over the page rather than beside it, which is `AppLayout`'s own
        * distinction. */}
      {(!isMobile || drawerOpen) && (
        <aside
          data-tower-sidebar
          className={cn(
            'fixed inset-y-0 left-0 z-40 w-64 bg-sidebar border-r border-sidebar-border flex flex-col',
            isMobile && 'shadow-2xl',
          )}
        >
          <div className="shrink-0 flex items-center gap-2 px-4 h-14 border-b border-sidebar-border">
            <Tower className="w-5 h-5 text-primary shrink-0" />
            <span className="font-display font-bold text-sidebar-foreground truncate">
              Control Tower
            </span>
          </div>

          <nav className="flex-1 overflow-y-auto py-2" aria-label={tt('Hlavná navigácia')}>
            {navSections().map((section) => (
              <div key={section.heading} className="mb-1">
                <p className="px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-sidebar-foreground/40">
                  {section.heading}
                </p>
                {section.items.map((item) => {
                  const active = location === item.href
                  return (
                    <Link
                      key={item.key}
                      href={item.href}
                      data-nav={item.key}
                      aria-current={active ? 'page' : undefined}
                    onClick={
                        /* The drawer still closes at the entry - §3.173's reason, unchanged. The active
                           entry is not guarded: it navigates nowhere, so the question would have no answer
                           behind it. */
                        active
                          ? () => setDrawerOpen(false)
                          : askFirst(() => {
                              setDrawerOpen(false)
                              navigate(item.href)
                            })
                      }
                        className={cn(
                        'flex items-center gap-2.5 px-4 min-h-[38px] text-sm transition-colors',
                        active
                          ? 'bg-primary/10 text-primary font-semibold border-r-2 border-primary'
                          : 'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-white/5',
                      )}
                    >
                      <item.Icon className="w-4 h-4 shrink-0" />
                      <span className="truncate">{item.label}</span>
                    </Link>
                  )
                })}
              </div>
            ))}
          </nav>

          <div className="shrink-0 border-t border-sidebar-border p-3 space-y-2">
            <p
              className="text-[11px] text-sidebar-foreground/60 truncate"
              data-tower-user
              title={userEmail}
            >
              {whoIsSignedIn}
            </p>
            <button
              type="button"
              data-sign-out
              onClick={askFirst(() => {
                signOutTower()
                window.location.assign('/')
              })}
              className="w-full min-h-[32px] rounded-lg border border-sidebar-border px-2 text-[11px] font-semibold text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-white/5 transition-colors flex items-center justify-center gap-1.5"
            >
              <LogOut className="w-3.5 h-3.5" />
              <T>Odhlásiť</T>
            </button>
          </div>
        </aside>
      )}

      {/* The backdrop, so a tap anywhere outside closes it - the second way out after a navigation,
        * and the one somebody reaches for first. Below the sidebar's z-40 and above the content. */}
      {isMobile && drawerOpen && (
        <div
          data-tower-backdrop
          className="fixed inset-0 z-30 bg-black/50"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      {/* The sidebar is fixed, so the content is inset by its width - and then it is simply the
        * width that is left, the same as the manager.
        *
        * §3.79, the owner: *"SaaS is centered, I hate that... Make it like in manager. Manager is
        * not centered with padding to sidebar that much."* `AppLayout` ends in
        * `<main className="p-4 sm:p-6">{children}</main>` with **no `max-w` and no `mx-auto`
        * anywhere**, so this is now the same two classes rather than an approximation of them.
        *
        * The comment that stood here claimed `max-w-6xl` had been moved *"inside that rather than
        * centred on the viewport"* - but `mx-auto` centres within whatever box it is in, so moving
        * it inside `pl-64` only changed which empty margins the owner was looking at. A half-fix
        * described as a fix is how this survived a rewrite of the whole shell. */}
      {/* A `div`, not a `main`, and that is not a detail: **all 19 Tower pages render their own
        * `<main>`** - counted, not assumed - so a `main` here would nest the landmark on every
        * screen. The manager can use `<main>` in `AppLayout` because its pages do not. Only the two
        * padding classes are shared, which is what the owner asked for. */}
      {/* §3.173 - the inset comes off on a phone, where there is nothing beside the content to inset
        * from. **That is the whole mechanism, and a mutation is what says so**: made unconditional again,
        * the dashboard scrolls sideways at 390 px.
        *
        * **`min-w-0` was here and is deliberately gone.** It was copied from legacy's
        * `<main className="lg:pl-64 flex-1 min-w-0">` - where it is load-bearing, because that `main` is a
        * **flex item** inside `<div className="lg:flex">` and would otherwise refuse to shrink below its
        * content. This wrapper is an ordinary block child, so the class does nothing at all, and dropping
        * it left all six cases green on all four screens. A class with a stated purpose it does not serve
        * is a claim the next reader trusts. What really keeps a wide table inside the page is `DataTable`'s
        * own `hidden md:block overflow-x-auto` and its `md:hidden` cards. */}
      <div data-tower-content className={cn(!isMobile && 'pl-64')}>
        {/* The mobile header, which this shell had none of. Sticky, so the way to the navigation is
          * reachable from anywhere on a long page - `AppLayout`'s reasoning, and legacy's `lg:hidden`
          * header does the same. */}
        {isMobile && (
          <header className="sticky top-0 z-20 flex items-center gap-3 h-14 px-4 bg-card/95 backdrop-blur border-b border-border">
            <button
              type="button"
              data-tower-drawer-toggle
              aria-label="Menu"
              aria-expanded={drawerOpen}
              onClick={() => setDrawerOpen((open) => !open)}
              className="p-2 -ml-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              {drawerOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <span className="font-display font-bold text-foreground truncate">Control Tower</span>
          </header>
        )}
        <div className="p-4 sm:p-6">{props.children}</div>
      </div>

      {/* §3.175, FB-941 - at the shell's root rather than inside the sidebar, because the sidebar is
        * conditional on a phone and a question that unmounts with the panel it was asked from is no
        * question at all.
        *
        * `ConfirmDialog` rather than `window.confirm`: it renders on 52 screens, it traps the keyboard
        * (§9.441), and it carries markers an e2e case can select. `danger`, because confirming discards
        * work - the same treatment the product gives a deletion. */}
      <ConfirmDialog
        open={pending !== null}
        onClose={() => setPending(null)}
        onConfirm={() => {
          const leave = pending?.run
          setPending(null)
          leave?.()
        }}
        title={tt('Odísť bez uloženia?')}
        description={tt(
          'Na tejto stránke máte neuložené zmeny. Ak odídete, stratia sa - uložte ich najprv.',
        )}
        confirmLabel={tt('Odísť a zahodiť')}
        cancelLabel={tt('Zostať na stránke')}
        variant="danger"
      />
      <Toaster />
    </div>
  )
}
